import React, { useEffect, useRef, useState } from "react"

import Vector2 from "../../utils/baseClasses/Vector2"

// Types
interface AnimatedStateObject {
  id: string
  text: string
}

interface AnimatedObject extends AnimatedStateObject {
  position: Vector2
  velocity: Vector2
  rotation: number

  attachedElement: HTMLSpanElement
}

// Props
export interface ElementExplosionAnimationProps {
  text: string
  fontSize?: string

  velocityMin?: Vector2
  velocityMax?: Vector2

  particleCount?: number
  particleLife?: number

  AIR_RESISTANCE?: number
  GRAVITY?: number

  onAnimationFinish?: () => void
}

const ElementExplosionAnimation: React.FC<ElementExplosionAnimationProps> = ({
  text,
  fontSize = "25px",

  velocityMin = new Vector2(),
  velocityMax = new Vector2(700, -300),

  particleCount = 10,
  particleLife = 1,

  AIR_RESISTANCE = 0.8,
  GRAVITY = 400,

  onAnimationFinish,
}) => {
  const [animatedStateObjects, setAnimatedStateObjects] = useState<
    AnimatedStateObject[]
  >([])
  const animatedObjectsRef = useRef<AnimatedObject[]>([])
  const animatedElementsContainerRef = useRef<HTMLDivElement>(null)

  const timePassed = useRef(0)

  // Generate objects to animate
  useEffect(() => {
    const newAnimatedObjects: AnimatedObject[] = []

    for (let i = 0; i < particleCount; i++) {
      newAnimatedObjects.push({
        position: new Vector2(),
        velocity:
          new Vector2(Math.random(), Math.random())
            .mul(Vector2.subVector2(velocityMax, velocityMin))
            .add(velocityMin),
        rotation: Math.random(),

        id: crypto.randomUUID(),
        text,

        attachedElement: null,
      })
    }

    animatedObjectsRef.current = newAnimatedObjects
    setAnimatedStateObjects(newAnimatedObjects)
  }, [])

  // Get object elements once generated in HTML
  useEffect(() => {
    if (animatedElementsContainerRef.current && animatedObjectsRef.current) {
      // Iterate through
      animatedObjectsRef.current.forEach(animatedObject => {
        if (!animatedObject.attachedElement) {
          // Get the element with the same ID
          const matchingElement = document.getElementById(
            animatedObject.id,
          ) as HTMLSpanElement
          if (matchingElement) {
            animatedObject.attachedElement = matchingElement
          }
        }
      })
    }
  }, [animatedStateObjects])

  // Animate objects
  useEffect(() => {
    let canceled = false

    if (animatedElementsContainerRef.current) {
      // Set up delta time for variable frame rate
      let oldTime = Date.now()
      let deltaTime = 0

      // Called every frame
      const updateFrame = () => {
        // Stop everything and trigger animation finish if canceled
        if (canceled) {
          if (onAnimationFinish) {
            onAnimationFinish()
          }
          return
        }

        // Update delta time
        const newTime = Date.now()
        deltaTime = (newTime - oldTime) / 1000
        timePassed.current += deltaTime
        oldTime = newTime

        // Stop if the amount of time passed is sufficient
        if (timePassed.current > particleLife) {
          canceled = true
        }

        // Animate each object
        animatedObjectsRef.current.forEach(animatedObject => {
          if (!animatedObject.attachedElement) return

          // Calculate velocity
          const velocityDelta = new Vector2(
            animatedObject.velocity.x * AIR_RESISTANCE,
            GRAVITY,
          )
          velocityDelta.mulNum(deltaTime)
          animatedObject.velocity.sub(velocityDelta)

          // Calculate position
          const positionDelta = new Vector2(
            animatedObject.velocity.x,
            -animatedObject.velocity.y,
          )
          positionDelta.mulNum(deltaTime)
          animatedObject.position.add(positionDelta)

          // Apply transform to element
          animatedObject.attachedElement.style.transform = `translate(${animatedObject.position.x}px, ${animatedObject.position.y}px) rotate(${animatedObject.rotation}turn)`
        })

        animatedElementsContainerRef.current.style.opacity = Math.min(
          (1 - timePassed.current / particleLife) * 2,
          1,
        ).toString()

        // Restart animation loop
        requestAnimationFrame(updateFrame)
      }

      // Begin animation loop
      requestAnimationFrame(updateFrame)
    }

    return () => {
      canceled = true
    }
  }, [animatedElementsContainerRef, animatedObjectsRef])

  return (
    <div
      ref={animatedElementsContainerRef}
      style={{
        position: "relative",
        width: "0",
        height: "0",
        margin: "0",
        padding: "0",

        zIndex: "10000",

        opacity: "1",
      }}
    >
      {animatedStateObjects.map(animatedStateObject => (
        <span
          key={animatedStateObject.id}
          id={animatedStateObject.id}
          style={{
            display: "block",
            position: "absolute",
            left: 0,
            top: 0,

            fontSize,
            transform: "translate(0px, 0px)",
          }}
        >
          {text}
        </span>
      ))}
    </div>
  )
}

export default ElementExplosionAnimation
